import { memo, useCallback, useMemo, useState } from 'react';

import { LayoutGridIcon, PlusIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';
import { useAirModal } from '@air/provider-modal';

import { CreateBoardModal } from '~/components/Modals/CreateBoardModal';
import { SubSideNavSectionHeader } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { WorkspaceSidebarWorkspaceBoardsMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarWorkspaceBoardsMoreDropdown';
import { BOARDS_SELECT_EMPTY_OPTION } from '~/constants/BoardSearch';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';
import { canChangeWorkspaceDetails } from '~/utils/permissions/workspacePermissions';

const CREATE_BOARD_PLUS_BTN_TOOLTIP = 'Create new board';

export const WorkspaceSidebarWorkspaceBoardsHeader = memo(() => {
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapse = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);
  const canUpdateSubnavSort = canChangeWorkspaceDetails(permissions);
  const [showCreateBoardModal] = useAirModal(CreateBoardModal);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const handleCreateRootBoard = useCallback(() => {
    showCreateBoardModal({
      selectedParentBoard: BOARDS_SELECT_EMPTY_OPTION,
      trackLocation: 'side-nav-extension',
    });
  }, [showCreateBoardModal]);

  const actions = useMemo(
    () => (
      <div className="group/workspace-sidebar-workspace-boards-header flex items-center gap-1">
        {canUpdateSubnavSort && <WorkspaceSidebarWorkspaceBoardsMoreDropdown onOpenChange={setIsMoreMenuOpen} />}
        {canCreateRootBoards && (
          <Tooltip label={CREATE_BOARD_PLUS_BTN_TOOLTIP} side="right">
            <IconButton
              adsIcon
              icon={PlusIcon}
              onClick={handleCreateRootBoard}
              size="small"
              color="gray"
              appearance="ghost"
              data-testid="WORKSPACE_SIDEBAR_CREATE_NEW_BOARD_BUTTON"
              label={CREATE_BOARD_PLUS_BTN_TOOLTIP}
            />
          </Tooltip>
        )}
      </div>
    ),
    [canCreateRootBoards, canUpdateSubnavSort, handleCreateRootBoard],
  );

  return (
    <SubSideNavSectionHeader
      actions={actions}
      collapsed={collapsed}
      data-testid="LIBRARIES_SUB_SIDE_NAV_BOARDS_HEADER"
      icon={LayoutGridIcon}
      isActionsVisible={isMoreMenuOpen}
      isRootDropTarget
      label="Boards"
      onToggle={toggleCollapse}
    />
  );
});

WorkspaceSidebarWorkspaceBoardsHeader.displayName = 'WorkspaceSidebarWorkspaceBoardsHeader';
